<template>
  <div class="analyze-sustainability">
    <div class="container">

      <!-- 永續度大圓 -->
      <section class="score-section">
        <D3PercentageDonutChart
          :percentage="sustainabilityScore"
          :size="180"
          :thickness="20"
          color="#7DC4B7"
        />
        <p class="score-desc fs-14px">{{ scoreDesc }}</p>
      </section>

      <!-- 三個小指標 -->
      <div class="stat-grid">
        <section class="stat-card">
          <div class="stat-title fs-14px">二手購入率</div>
          <div class="stat-value">
            <span class="num">{{ secondHandRate }}</span><span class="unit">%</span>
          </div>
          <div class="stat-sub fs-12px">
            <template v-if="totalWithStatus > 0">
              {{ secondHandCount }} / {{ totalWithStatus }} 件已知購入狀態
            </template>
            <template v-else>尚未填寫購入狀態</template>
          </div>
        </section>

        <section class="stat-card">
          <div class="stat-title fs-14px">環境友善材質</div>
          <div class="stat-value">
            <span class="num">{{ ecoMaterialRate }}</span><span class="unit">%</span>
          </div>
          <div class="stat-sub fs-12px">
            <template v-if="totalWithMaterial > 0">
              {{ ecoCount }} / {{ totalWithMaterial }} 件已標記材質
            </template>
            <template v-else>尚未填寫材質</template>
          </div>
        </section>

        <section class="stat-card">
          <div class="stat-title fs-14px">平均穿搭次數</div>
          <div class="stat-value">
            <span class="num">{{ avgWearCount.toFixed(1) }}</span><span class="unit">次</span>
          </div>
          <div class="stat-sub fs-12px">使用中的單品平均</div>
        </section>
      </div>

      <!-- 淘汰流向（長條圖） -->
      <section class="recycle-section" v-if="totalRecycled > 0">
        <div class="recycle-header">
          <h3 class="section-title fs-16px fw-bold">已淘汰單品的去向</h3>
          <span class="fs-12px text-secondary">共 {{ totalRecycled }} 件</span>
        </div>
        <div class="recycle-bars">
          <div v-for="t in recycleStats" :key="t.label" class="recycle-row">
            <div class="recycle-name fs-14px">{{ t.label }}</div>
            <CustomHorizontalProgressBar
              :percentage="t.pct"
              :count="t.count"
              unit=" 件"
              :color="t.color"
            />
          </div>
        </div>
      </section>

      <section class="empty-section" v-else>
        <p class="fs-14px text-secondary">尚未有淘汰紀錄。試著回收或捐贈不再穿的單品，提升永續度！</p>
      </section>

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import D3PercentageDonutChart from '@/views/test/D3PercentageDonutChart.vue';
import CustomHorizontalProgressBar from '@/components/CustomHorizontalProgressBar.vue';

export default {
  name: 'AnalyzeSustainability',
  components: {
    D3PercentageDonutChart,
    CustomHorizontalProgressBar,
  },
  computed: {
    ...mapGetters(['clothWithWearCount']),
    cloths() {
      return this.clothWithWearCount || [];
    },

    totalWithStatus() {
      return this.cloths.filter(c => c.getStatus !== -1 && c.getStatus !== undefined).length;
    },
    secondHandCount() {
      return this.cloths.filter(c => c.getStatus === 1).length;
    },
    secondHandRate() {
      if (this.totalWithStatus === 0) return 0;
      return Math.round(this.secondHandCount / this.totalWithStatus * 100);
    },

    totalWithMaterial() {
      return this.cloths.filter(c => c.material !== -1 && c.material !== undefined).length;
    },
    ecoCount() {
      return this.cloths.filter(c => c.material === 8).length;
    },
    ecoMaterialRate() {
      if (this.totalWithMaterial === 0) return 0;
      return Math.round(this.ecoCount / this.totalWithMaterial * 100);
    },

    inUseCloths() {
      return this.cloths.filter(c => !c.isDeleted && c.recycleType === -1);
    },
    avgWearCount() {
      if (this.inUseCloths.length === 0) return 0;
      const total = this.inUseCloths.reduce((s, c) => s + (c.wearCount || 0), 0);
      return total / this.inUseCloths.length;
    },

    recycledCloths() {
      return this.cloths.filter(c => c.recycleType !== undefined && c.recycleType >= 0);
    },
    totalRecycled() {
      return this.recycledCloths.length;
    },
    recycleStats() {
      const types = [
        { type: 0, label: '捐贈', color: '#7DC4B7' },
        { type: 1, label: '二手販售', color: '#F2C31C' },
        { type: 2, label: '舊衣回收', color: '#AAD272' },
        { type: 4, label: '其他', color: '#C4C4C4' },
        { type: 3, label: '垃圾處理', color: '#DE6167' },
      ];
      const total = this.totalRecycled;
      return types.map(t => {
        const count = this.recycledCloths.filter(c => c.recycleType === t.type).length;
        return {
          ...t,
          count,
          pct: total > 0 ? Math.round(count / total * 100) : 0,
        };
      }).filter(t => t.count > 0);
    },
    sustainableRecycleRate() {
      if (this.totalRecycled === 0) return null;
      const sustainable = this.recycledCloths.filter(c => [0, 1, 2].includes(c.recycleType)).length;
      return sustainable / this.totalRecycled * 100;
    },

    sustainabilityScore() {
      const components = [];
      if (this.totalWithStatus > 0) components.push(this.secondHandRate);
      if (this.totalWithMaterial > 0) components.push(this.ecoMaterialRate);
      if (this.sustainableRecycleRate !== null) components.push(this.sustainableRecycleRate);
      const wearScore = Math.min(100, this.avgWearCount * 10);
      components.push(wearScore);
      if (components.length === 0) return 0;
      const score = components.reduce((a, b) => a + b, 0) / components.length;
      return Math.round(score);
    },

    scoreDesc() {
      const s = this.sustainabilityScore;
      if (s >= 80) return '太棒了！您的衣櫥非常永續';
      if (s >= 60) return '不錯！還有進步的空間';
      if (s >= 40) return '一般，可以多嘗試二手或回收';
      if (s > 0) return '建議從二手購入或回收舊衣開始';
      return '開始記錄您的衣櫥資料，看看永續度吧';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/basic";

.analyze-sustainability {
  padding: .5rem 0 2rem;
}

// ===== 永續度大圓 =====
.score-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 0 2rem;
}

.score-desc {
  color: $typography-secondary-default;
  text-align: center;
  margin: 0;
}

// ===== 小指標 grid =====
.stat-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: .75rem;
  margin-bottom: 1.5rem;
  @media (min-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.stat-card {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 1rem 1.25rem;

  .stat-title {
    color: $typography-secondary-default;
    margin-bottom: .25rem;
  }
  .stat-value {
    color: $typography-primary-default;
    margin-bottom: .25rem;
    .num {
      font-size: 1.75rem;
      font-weight: bold;
    }
    .unit {
      font-size: 1rem;
      color: $typography-secondary-default;
      margin-left: 2px;
    }
  }
  .stat-sub {
    color: $typography-secondary-default;
  }
}

// ===== 淘汰流向（長條圖） =====
.recycle-section {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 1.25rem;
}

.recycle-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;

  .section-title {
    margin: 0;
    color: $typography-primary-default;
  }
  .text-secondary {
    color: $typography-secondary-default;
  }
}

.recycle-bars {
  display: flex;
  flex-direction: column;
  gap: .75rem;
}

.recycle-row {
  .recycle-name {
    margin-bottom: .25rem;
    color: $typography-primary-default;
  }
}

// ===== 空狀態 =====
.empty-section {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 2rem 1.25rem;
  text-align: center;

  .text-secondary {
    color: $typography-secondary-default;
    margin: 0;
  }
}

.text-secondary {
  color: $typography-secondary-default;
}
</style>
